import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { downloadFile, logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { CallHistoryApi } from 'entities/CallHistory/api/callHistory'
import {
  type ICallHistoryExportFormat,
  type IParamsExportCallHistory,
  type IParamsGetCallHistory,
} from 'entities/CallHistory/api/types'
import { ExportCallHistoryModalContent } from '../ui/ExportCallHistoryModalContent'
import { ExportCallHistoryModalActions } from '../ui/ExportCallHistoryModalActions'

type IExportParams = Omit<IParamsGetCallHistory, 'page'>

export class ExportCallHistoryModalStore {
  private _format: ICallHistoryExportFormat | null = null
  private _loadingExport = false
  private _idModal = ''
  private _exportParams: IExportParams | null = null

  constructor() {
    makeAutoObservable(this)
  }

  setFormat = (format: ICallHistoryExportFormat) => {
    this._format = format
  }

  onExport = async () => {
    try {
      if (this._format && this._exportParams) {
        this._loadingExport = true

        const params: IParamsExportCallHistory = {
          ...this._exportParams,
          format: this._format,
        }

        const { data } = await CallHistoryApi.exportCallHistory(params)

        if (data.url) {
          toastStore.add({
            id: nanoid(),
            type: 'success',
            title: 'The export is emailed to you',
            desc: "If you don't see your file, click download",
            action: {
              onAction: () => {
                if (data.url) downloadFile(data.url)
              },
              text: 'Download',
            },
          })
        } else {
          toastStore.add({
            id: nanoid(),
            type: 'success',
            title: 'The export is emailed to you',
          })
        }
      }
    } catch (e) {
      logger.error(e)
      toastStore.add({
        id: nanoid(),
        type: 'error',
        title: 'Something went wrong, try again!',
      })
    } finally {
      this._loadingExport = false
    }
    modalStore.closeModal(this._idModal)
  }

  onOpenExport = (exportParams: IExportParams) => {
    this._format = null
    this._exportParams = exportParams
    this._idModal = nanoid()

    modalStore.addModal({
      id: this._idModal,
      title: 'Export call history',
      ModalActions: ExportCallHistoryModalActions,
      ModalContent: ExportCallHistoryModalContent,
      ModalContentProps: { store: this },
    })
  }

  get format() {
    return this._format
  }

  get disabled() {
    return !this._format || this._loadingExport
  }

  get loadingExport() {
    return this._loadingExport
  }

  dispose = () => {
    this._format = null
    this._loadingExport = false
    this._idModal = ''
    this._exportParams = null
  }
}
